import {
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import moment from 'moment';
import { fromEvent, of, ReplaySubject } from 'rxjs';
import { delay, takeWhile, throttleTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as fromDataServices from '../../data-services';
import { ITask } from '../../lookups';
import { PayrollItem } from '../../payroll/item/store';
import * as fromShared from '../../shared';
import { DisplayMessageMinimum, ResponseStatus } from '../../shared';
import { SelectItem } from '../../shared/efaact-primeng.module';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { UserAuthorization } from '../../user-authorizations';
const _log = new LoggingService();
export const percentOrAmountValidator: ValidatorFn = (
    control: AbstractControl
): ValidationErrors | null => {
    if (!control) {
        return null;
    }
    const amount = control.get('PremiumPay');
    const percent = control.get('PremiumPercent');
    _log.verbose({
        type: 'percentOrAmountValidator',
        amount,
        percent,
        invalid: amount && percent && amount.value && percent.value
    });
    return amount && percent && amount.value && percent.value
        ? { percentOrAmount: 'Only one of percent or amount can be specified.' }
        : null;
};
export const endGreaterThanStartValidator: ValidatorFn = (
    control: AbstractControl
): ValidationErrors | null => {
    if (!control) {
        return null;
    }
    const startDate = moment(control.get('StartDate').value);
    const endDate = moment(control.get('EndDate').value);
    if (!(startDate && endDate)) {
        return null;
    }
    _log.verbose({
        type: 'endGreaterThanStartValidator',
        startDate,
        endDate
    });
    return startDate.isAfter(endDate)
        ? { endGreaterThanStart: 'End Date must be >= start date.' }
        : null;
};

@Component({
    selector: 'ef-premium-pay',
    templateUrl: './premium-pay.component.html',
    styleUrls: ['./premium-pay.component.scss'],
    standalone: false
})
export class PremiumPayComponent
    extends fromShared.Errors
        .DisplayErrors<fromDataServices.PremiumPay.PremiumPay>
    implements OnInit, OnDestroy {
    dropdownItemHeight = environment.dropdownItemHeight;
    @Input() userAuths: UserAuthorization[];
    @Input() tasks: ITask[];
    @Input() payrollItems: PayrollItem[];
    @Input() tasksFilterSelectItems: SelectItem[];
    @Input() alternateTasksSelectItems: SelectItem[];
    @Input() tasksSelectItems: SelectItem[];
    @Input() costTypesSelectItems: SelectItem[];
    @Input() usersSelectItems: SelectItem[];
    @Input() payrollItemsSelectItems: SelectItem[];
    @Input() payrollItemsFilterSelectItems: SelectItem[];
    @Input() serviceItemsSelectItems: SelectItem[];
    @Input() directReportsSelectItems: SelectItem[];
    @Output() Filter = new ReplaySubject<{
        PPEndDate: Date;
        DirectReports?: [{ EmployeeListID: string }];
        Tasks?: [{ TaskListID: string }];
    }>(1);
    @Output() Save = new ReplaySubject<fromDataServices.PremiumPay.PremiumPay>(
        1
    );
    @Output() Delete = new ReplaySubject<
        fromDataServices.PremiumPay.PremiumPay[]
    >(1);
    @Output() Select =
        new ReplaySubject<fromDataServices.PremiumPay.PremiumPay>(1);
    @ViewChild('deletePremiumPays', { read: ElementRef, static: true })
    deleteButton: ElementRef;
    @ViewChild('updatePremiumPays', { read: ElementRef, static: true })
    updateButton: ElementRef;
    @ViewChild('addPremiumPays', { read: ElementRef, static: true })
    addButton: ElementRef;
    @ViewChild('clearFilters', { read: ElementRef, static: true })
    clearButton: ElementRef;
    public editing: fromDataServices.PremiumPay.PremiumPay =
        {} as fromDataServices.PremiumPay.PremiumPay;
    selected: fromDataServices.PremiumPay.PremiumPay[] = [];
    cloned: { [i: number]: fromDataServices.PremiumPay.PremiumPay } = {};
    filterFormGroup: UntypedFormGroup;
    addFormGroup: UntypedFormGroup;
    moment: any;
    public displayDetails = true;
    private active = true;
    formAddErrors: {
        severity: string;
        detail: string;
    }[];
    constructor(
        private log: LoggingService,
        public confirmservice: UnsavedChangesService
    ) {
        super();
        this.moment = moment;
        this.filterFormGroup = new UntypedFormGroup({
            // DirectReports: new FormControl(),
            Tasks: new UntypedFormControl(),
            PPEndDate: new UntypedFormControl(new Date(), Validators.required)
        });
        this.addFormGroup = new UntypedFormGroup(
            {
                StartDate: new UntypedFormControl(null, Validators.required),
                EndDate: new UntypedFormControl(null, Validators.required),
                PremiumPercent: new UntypedFormControl(null),
                PremiumPay: new UntypedFormControl(null),
                PremiumTaskListID: new UntypedFormControl(null),
                CalcType: new UntypedFormControl(null)
            },
            {
                validators: [
                    percentOrAmountValidator,
                    endGreaterThanStartValidator
                ]
            }
        );
    }
    operators: [];

    ngOnInit(): void {
        this.setupSubscriptions();
        setTimeout(() => {
            this.handleOnOpen(0);
        }, 0);
    }
    ngOnDestroy() {
        this.active = false;
    }
    setupSubscriptions() {
        this.filterFormGroup.valueChanges
            .pipe(takeWhile(() => this.active))
            .subscribe(values => {
                this.log.verbose({
                    type: 'PremiumPayComponent:filterFormGroup.valueChanges',
                    values
                });
                if (moment(values.PPEndDate).isValid()) {
                    this.Filter.next({
                        PPEndDate: values.PPEndDate,
                        Tasks:
                            values.Tasks && values.Tasks.length
                                ? values.Tasks.map(x => ({
                                      TaskListID: x,
                                      ContractType: '',
                                      IsActive: true,
                                      IsChargeNumber: false,
                                      LaborType: '',
                                      TaskLongName: ''
                                  }))
                                : null
                        // DirectReports: values.DirectReports && values.DirectReports.length
                        //     ? values.DirectReports.map(x => ({
                        //           EmployeeListID: x
                        //       }))
                        //     : null
                    });
                }

            });

        fromEvent(this.deleteButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                throttleTime(500)
            )
            .subscribe(() => {
                this.confirmservice
                    .confirmPromise({
                        dialogText: `Are you sure you want to delete these rates?`
                    })
                    .then(() => {
                        // if (item.id === 'add') {
                        //     this.items.splice(index, 1, { ...this.addTemplate } as any);
                        // } else {
                        this.log.verbose({
                            type: 'PremiumPayComponent:deleteButton',
                            cloned: this.cloned,
                            selected: this.selected
                        });
                        this.Delete.next(this.selected);
                        return;
                    })
                    .catch(e => {});
            });
        fromEvent(this.addButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                throttleTime(500)
            )
            .subscribe(() => {
                const values = this.addFormGroup.value;
                this.log.verbose({
                    type: 'PremiumPayComponent:addButton',
                    values
                });
                of(...this.selected).subscribe(pp =>
                    this.Save.next({
                        ...pp,
                        Id: -1,
                        id: -1,
                        original: pp,
                        StartDate: values.StartDate,
                        EndDate: values.EndDate,
                        PremiumPay: values.PremiumPay,
                        PremiumPercent: values.PremiumPercent,
                        CalcType: values.CalcType
                    })
                );
                this.selected = [];
            });
        fromEvent(this.updateButton.nativeElement, 'click')
            .pipe(
                takeWhile(() => this.active),
                throttleTime(500)
            )
            .subscribe(() => {
                const values = this.addFormGroup.value;
                this.log.verbose({
                    type: 'PremiumPayComponent:updateButton',
                    values
                });
                this.items = this.items.map(x => ({
                    ...x,
                    Status:
                        this.selected.findIndex(y => y.id === x.id) >= 0
                            ? {
                                  StatusCode: ResponseStatus.Loading,
                                  Message: 'Saving'
                              }
                            : x.Status
                }));
                of(...this.selected)
                    .pipe(delay(2000))
                    .subscribe(pp =>
                        this.Save.next({
                            ...pp,
                            original: pp,
                            StartDate:
                                values && values.StartDate
                                    ? values.StartDate
                                    : pp.StartDate,
                            EndDate:
                                values && values.EndDate
                                    ? values.EndDate
                                    : pp.EndDate,
                            PremiumPay:
                                values && values.PremiumPay
                                    ? values.PremiumPay
                                    : pp.PremiumPay,
                            PremiumPercent:
                                values && values.PremiumPercent
                                    ? values.PremiumPercent
                                    : pp.PremiumPercent,
                            CalcType:
                                values && values.CalcType
                                    ? values.CalcType
                                    : pp.CalcType
                        })
                    );
                this.selected = [];
            });

        fromEvent(this.clearButton.nativeElement, 'click')
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.addFormGroup.reset();
            });
        this.addFormGroup.valueChanges
            .pipe(takeWhile(() => this.active))
            .subscribe(values => {
                this.formAddErrors = this.getErrorsAsArray(
                    this.addFormGroup.errors
                );
            });
    }
    onRowEditInit(item: fromDataServices.PremiumPay.PremiumPay) {
        this.cloned[item.id] = { ...item };
        this.editing = item;
        this.log.verbose({
            type: 'PremiumPayComponent:onRowEditInit',
            item,
            cloned: this.cloned,
            editing: this.editing
        });
        this.selected = [];
    }
    onRowEditSave(item: fromDataServices.PremiumPay.PremiumPay) {
        this.log.verbose({
            type: 'PremiumPayComponent:onRowEditSave',
            item,
            cloned: this.cloned,
            selected: this.selected
        });
        item.Status.StatusCode = ResponseStatus.Loading;
        item.Status.Message = 'Saving';

        this.Save.next(item);
        delete this.cloned[item.id];
        delete this.editing;
        this.selected = [];
    }
    onRowEditCancel(item: fromDataServices.PremiumPay.PremiumPay) {
        const index = this.items.findIndex(x => x.id === item.id);
        if (index >= 0) {
            this.items.splice(index, 1, this.cloned[item.id]);
        }
        this.items = [...this.items];
        this.selected = [];

        delete this.cloned[item.id];
        delete this.editing;
    }
    onRowSelect($event) {
        this.log.verbose({ type: 'PremiumPayComponent:onRowSelect', $event });
        this.Select.next($event.data);
    }
    onRowEditDelete(
        item: fromDataServices.PremiumPay.PremiumPay,
        index: number
    ) {
        this.confirmservice
            .confirmPromise({
                dialogText: `Are you sure you want to delete this rate?`
            })
            .then(() => {
                // if (item.id === 'add') {
                //     this.items.splice(index, 1, { ...this.addTemplate } as any);
                // } else {
                this.items.splice(index, 1, this.cloned[item.id]);
                // }

                this.Delete.next([item]);

                delete this.cloned[item.id];
                delete this.editing;
                return;
            })
            .catch(e => {});
    }
    handleOnOpen($event) {
        this.log.verbose({ type: 'PremiumPayComponent:handleOnOpen', $event });
        const baseHeight = '52vh';
        let accHeight = 0;
        if ($event === 0 || ($event.includes && $event.includes(0))) {
            accHeight += 5.5;
        }
        if ($event === 1 || ($event.includes && $event.includes(1))) {
            accHeight += 10;
        }
        this.dataTable.scrollHeight = `calc(${baseHeight} - ${accHeight}rem)`;
        this.log.verbose({
            type: 'PremiumPayComponent:handleOnOpen',
            datatableHeight: this.dataTable.scrollHeight
        });
    }
    public hasErrorStatus = pp =>
        pp && pp.Status && pp.Status.StatusCode >= DisplayMessageMinimum;

    //public getMinDate = (date1, date2) => new Date(Math.min(date1, date2));
    getMinDate(date1, date2) {
        const dates = [date1, date2];
        dates.sort((a, b) => a.getTime() - b.getTime());
        return dates[0];
    }
    getMaxDate(date1, date2) {
        const dates = [date1, date2];
        dates.sort((a, b) => b.getTime() - a.getTime());
        return dates[0];
    }
    //public getMaxDate = (date1, date2) => new Date(Math.max(date1, date2));
    //        severity: string;
    // summary: string;
    // detail: string;
    public getErrorsAsArray = (errors: any) => {
        if (!errors) {
            return [];
        }
        const keys = Object.keys(errors);
        _log.verbose({
            type: 'getErrorsAsArray',
            keys,
            errors,
            values: keys.map(k => ({
                severity: 'error',
                detail: errors[k].toString()
            }))
        });
        return keys.map(k => ({
            severity: 'error',
            detail: errors[k].toString()
        }));
    };
}
